import type {
  ExternalDesignKnowledgeLibraryGapCode,
  LibraryGapCode,
} from '@cogo/agentic-generator'
import type { UxApplicationLibraryAdmissionIssueCode } from '@cogo/kg-browser'
import {
  KO_DESIGN_AXES_COPY,
  type DesignAxesCopy,
} from '../design-system/source-derived/axes-copy'
import type { Advice, AxisName, AxisResolution } from '../design-system/source-derived/types'

export interface ReadinessItem {
  kind: 'blocking' | 'unfilled' | 'advisory'
  title: string
  explanation: string
  machineCode?: string
  suggestion?: { label: string; navigateTo?: string }
  evidenceRefs: string[]
}

/**
 * 세 어휘(`LibraryGapCode`·`ExternalDesignKnowledgeLibraryGapCode`·
 * `UxApplicationLibraryAdmissionIssueCode`)가 공유하는 최소 입력 형태.
 *
 * 주의(브리프 서술 정정): `code`/`path`/`message`/`evidenceRefs` 를 "세 타입이
 * 이미 공유한다"는 것은 `LibraryGap`·`ExternalDesignKnowledgeLibraryGap` 에는
 * 맞지만 `UxApplicationLibraryAdmissionIssue`(@cogo/kg-browser) 에는 맞지
 * 않는다 — 그 타입은 `evidenceRefs` 가 없고 대신 `focusNode?: string` 을 갖는다
 * (packages/cogo-kg-browser/src/ux-application-library-admission.ts:37-42).
 * 이 함수를 그 타입의 값으로 호출하려는 쪽(Task 3 의 collectMachineGaps)은
 * `evidenceRefs: issue.focusNode ? [issue.focusNode] : []` 로 적응해야 한다 —
 * 이 파일은 그 적응을 대신 하지 않는다(입력 형태를 넓히면 세 어휘 중 둘이 이미
 * 구조적으로 만족하는 이 간단한 계약을 잃는다).
 */
interface MachineGapLike {
  code: string
  path: string
  message: string
  evidenceRefs: readonly string[]
}

interface MachineGapCopy {
  readonly title: string
  readonly explanation: string
  readonly suggestion?: ReadinessItem['suggestion']
}

const UNKNOWN_GAP_TITLE = '알 수 없는 항목이 생성을 막고 있습니다'

const REGISTER_SOURCE_SUGGESTION = {
  label: '소스 등록으로 이동',
  navigateTo: '/studio/import',
} as const satisfies ReadinessItem['suggestion']

/**
 * `LibraryGapCode` 23종 — 정본:
 * packages/cogo-agentic-generator/src/library-composition/types.ts:403-426
 *
 * `satisfies Record<LibraryGapCode, MachineGapCopy>` 는 이 표가 그 유니언의
 * 멤버를 하나도 빠짐없이, 하나도 더하지 않고 덮는다는 것을 컴파일 타임에
 * 강제한다 — 유니언에 코드가 추가/삭제되면 이 리터럴이 즉시 컴파일 에러가
 * 된다(초과 프로퍼티 검사 + 누락 프로퍼티 검사, `tsc --noEmit` 이 잡는다).
 */
const LIBRARY_GAP_COPY = {
  'library-input-schema-invalid': {
    title: '생성 입력 데이터의 형식이 올바르지 않습니다',
    explanation:
      '이 화면을 만드는 데 필요한 데이터가 예상된 형식과 다릅니다. 프로젝트를 다시 불러오면 해결되는 경우가 많습니다.',
  },
  'library-source-forbidden': {
    title: '이 소스는 생성에 쓸 수 없습니다',
    explanation:
      '출처와 권리가 확인되지 않은 자료가 섞여 있습니다. 소스의 출처와 권리를 먼저 등록해야 이 화면을 만들 수 있습니다.',
    suggestion: REGISTER_SOURCE_SUGGESTION,
  },
  /**
   * 발신부 전수(2026-08-01 확인) — 이 코드는 "기록이 비어 있다" 하나가 아니라
   * **출처(계보) 권위가 요건을 만족하지 못한다**는 한 갈래 전체를 가리킨다:
   *
   * - admit-library-composition.ts:113  — 권위 컨텍스트를 읽을 수 없음
   * - admit-library-composition.ts:545  — 권위 컨텍스트가 아예 공급되지 않음
   * - admit-library-composition.ts:648  — 권위 컨텍스트에 승인 목록이 불완전
   * - admit-library-composition.ts:1104 — **권위의 종류가 틀림**
   *   ("Canonical UX lineage may not use project-validation authority")
   * - admit-library-composition.ts:1160 — 같은 것의 Application 판
   * - finance-ux-function-unit-readiness.ts:156 — 사람이 쓴 정본 동작 기록 부재
   *
   * 이전 문구("출처 기록이 없습니다 / 소스를 다시 등록하면 기록이 채워집니다"
   * + 소스 재등록 suggestion)는 앞 세 경우에만 맞고 :1104/:1160 에서는 거짓
   * 안내였다 — 권위의 **종류**가 틀린 것은 같은 소스를 다시 등록해도 그대로다.
   * 그래서 (a) 복구 방법을 단정하지 않고 무엇이 위반됐는지를 말하며,
   * (b) suggestion 을 뺐다: 틀린 복구로 보내느니 없는 편이 낫다.
   */
  'library-lineage-missing': {
    title: '출처(계보) 권위 기록이 요건을 충족하지 못합니다',
    explanation:
      '생성에 쓰인 패턴·화면 정의가 어디서 왔는지 확인해 주는 근거가 요건에 맞지 않습니다. 근거가 없거나, 읽을 수 없거나, 이 자리에 쓸 수 없는 종류의 권위일 수 있습니다(예: 프로젝트 검증 권위는 정본 계보의 근거가 될 수 없습니다). 함께 표시된 위치와 기계 코드로 어느 경우인지 확인해 주세요.',
  },
  'ux-pattern-not-admitted': {
    title: '사용하려는 화면 패턴이 아직 검증되지 않았습니다',
    explanation:
      '이 패턴은 사용 가능한 것으로 확인되지 않았습니다. 검증된 다른 패턴을 고르거나, 검증이 끝나기를 기다려야 합니다.',
  },
  'application-blueprint-not-admitted': {
    title: '이 앱의 전체 설계가 아직 검증되지 않았습니다',
    explanation:
      '화면들을 하나로 묶는 앱 설계가 사용 가능한 것으로 확인되지 않았습니다. 검증이 끝난 뒤에 다시 시도해 주세요.',
  },
  'duplicate-ux-pattern-ref': {
    title: '같은 화면 패턴이 중복 참조되고 있습니다',
    explanation:
      '하나의 화면 패턴이 같은 자리에서 두 번 이상 정의되어 있어 어떤 것을 써야 할지 알 수 없습니다.',
  },
  'duplicate-ux-instance-id': {
    title: '화면 구성 요소의 이름이 겹칩니다',
    explanation: '같은 이름을 가진 화면 구성 요소가 두 개 이상 있어 서로 구분할 수 없습니다.',
  },
  'duplicate-library-port-id': {
    title: '데이터 연결 지점의 이름이 겹칩니다',
    explanation:
      '화면이 데이터를 주고받는 연결 지점(포트) 중 이름이 같은 것이 두 개 이상 있어 어디로 연결해야 할지 알 수 없습니다.',
  },
  'unbound-pattern-port': {
    title: '연결되어야 할 자리가 비어 있습니다',
    explanation:
      '화면이 데이터를 주고받으려면 반드시 연결되어야 하는 자리인데, 아무것도 연결되어 있지 않습니다.',
  },
  'incompatible-pattern-port': {
    title: '서로 맞지 않는 두 곳을 연결하려 합니다',
    explanation: '데이터를 주고받는 두 연결 지점의 방향이나 종류가 서로 맞지 않습니다.',
  },
  'cross-scope-local-reference': {
    title: '다른 화면의 내부 값을 직접 참조하고 있습니다',
    explanation:
      '한 화면 안에서만 유효해야 할 값을 다른 화면 범위에서 직접 가져다 쓰고 있습니다. 화면 사이의 데이터 전달은 정해진 연결 지점을 통해서만 가능합니다.',
  },
  'ux-pattern-state-invalid': {
    title: '화면 상태 정의에 문제가 있습니다',
    explanation:
      '화면이 가질 수 있는 상태(예: 대기·성공·실패) 중 일부가 올바르게 정의되어 있지 않습니다.',
  },
  'library-event-invalid': {
    title: '화면의 이벤트 연결이 어긋나 있습니다',
    explanation: '버튼이나 동작이 가리키는 이벤트가 실제로는 존재하지 않습니다.',
  },
  'library-capability-invalid': {
    title: '화면이 필요로 하는 기능이 선언되어 있지 않습니다',
    explanation: '화면이 사용하려는 기능이 앱 전체에 선언된 기능 목록에 없습니다.',
  },
  'ux-pattern-transition-invalid': {
    title: '화면 상태 전환에 문제가 있습니다',
    explanation:
      '한 상태에서 다른 상태로 넘어가는 경로가, 존재하지 않는 상태나 이벤트를 가리키고 있습니다.',
  },
  'journey-entry-invalid': {
    title: '앱의 시작 지점이 올바르지 않습니다',
    explanation: '사용자 여정이 시작되어야 할 화면이 실제로는 존재하지 않습니다.',
  },
  'journey-terminal-missing': {
    title: '앱의 종료 지점이 올바르지 않습니다',
    explanation: '사용자 여정이 끝나야 할 화면 중 일부가 존재하지 않거나 도달할 수 없습니다.',
  },
  'journey-step-unreachable': {
    title: '도달할 수 없는 화면이 있습니다',
    explanation:
      '시작 지점에서부터 이동해도 다다를 수 없는 화면이 있습니다. 화면 사이의 이동 경로를 다시 확인해야 합니다.',
  },
  'journey-recovery-invalid': {
    title: '오류 발생 후 되돌아오는 경로가 올바르지 않습니다',
    explanation:
      '실패했을 때 사용자를 다시 정상 흐름으로 데려오는 경로가 끊겨 있거나, 존재하지 않는 곳을 가리킵니다.',
  },
  'exact-namespace-collision': {
    title: '같은 이름이 한 곳에서 두 번 이상 쓰였습니다',
    explanation:
      '하나의 화면 또는 앱 안에서 고유해야 할 이름이 중복되어 있어 서로 구분할 수 없습니다.',
  },
  /**
   * 발신부 전수(2026-08-01 확인) — 이 코드는 **두 상황**을 함께 가리킨다:
   *
   * (표현 오염)
   * - admit-library-composition.ts:1596 — 스키마 additionalProperties 중
   *   class/style/color/coordinate/position/… 같은 표현 속성이 구조 안에 있음
   * - cogo-kg-browser/src/ux-application-library-admission.ts:1987 —
   *   SHACL 위반 경로가 표현 경로일 때
   *
   * (게시 차단)
   * - finance-ux-function-unit-readiness.ts:186 —
   *   "Publication remains blocked for this research handoff."
   * - finance-transaction-search-promotion.ts:145 —
   *   "Publication remains blocked for this reference-only pilot."
   *
   * 이전 문구("화면 구조 안에 디자인 값이 섞여 있습니다")는 앞의 둘만 설명해,
   * 발신부의 절반에서 사용자에게 사실이 아닌 원인을 알려주고 있었다. 한 코드가
   * 두 상황을 뜻하므로 문구도 그것을 반영한다 — 어느 쪽인지는 항목에 함께
   * 실리는 위치(path)와 기계 코드로 가른다.
   */
  'library-presentation-forbidden': {
    title: '표현·게시에 대한 계약 제한을 벗어났습니다',
    explanation:
      '이 코드는 두 상황 중 하나를 뜻합니다. 화면의 동작 구조 안에 색상·간격·위치 같은 디자인 값이 들어가 있거나(디자인은 디자인시스템에서 따로 관리됩니다), 지금 계약이 허용하지 않는 게시(publication)를 요청했습니다. 함께 표시된 위치와 기계 코드로 어느 쪽인지 확인해 주세요.',
  },
  'application-capability-mismatch': {
    title: '선언한 기능과 실제로 쓰는 기능이 다릅니다',
    explanation: '앱이 요청한 기능과 실제 화면들이 사용하는 기능이 서로 일치하지 않습니다.',
  },
  'application-webmcp-capability-unavailable': {
    title: '이 앱이 요청한 외부 연동 기능을 지금 쓸 수 없습니다',
    explanation:
      '다른 프로그램이 이 앱을 제어하거나 데이터를 주고받는 기능이 요청되었지만, 지금은 제공할 수 없는 상태입니다.',
  },
} satisfies Record<LibraryGapCode, MachineGapCopy>

/**
 * `ExternalDesignKnowledgeLibraryGapCode` 6종 — 정본:
 * packages/cogo-agentic-generator/src/continuous-knowledge/astryx-library-seam.ts:70-76
 */
const EXTERNAL_DESIGN_GAP_COPY = {
  'external-design-library-input-invalid': {
    title: '가져온 디자인 자료의 형식이 올바르지 않습니다',
    explanation:
      '외부에서 가져온 디자인 자료의 데이터 형식이 예상과 다릅니다. 자료를 다시 가져와 보세요.',
    suggestion: REGISTER_SOURCE_SUGGESTION,
  },
  'external-design-selection-rights-invalid': {
    title: '가져온 디자인 요소의 사용 권리가 확인되지 않았습니다',
    explanation:
      '이 디자인 요소를 실제로 사용해도 되는지 증빙이 맞지 않습니다. 소스를 다시 등록해 권리를 확인해야 합니다.',
    suggestion: REGISTER_SOURCE_SUGGESTION,
  },
  'external-design-business-behavior-gap-missing': {
    title: '가져온 디자인만으로는 동작을 알 수 없다는 표시가 없습니다',
    explanation:
      '디자인 이미지나 파일만으로는 버튼을 눌렀을 때 실제로 무슨 일이 일어나는지 알 수 없습니다. 이 사실이 함께 표시되어야 하는데 빠져 있습니다.',
  },
  'external-design-business-behavior-not-observed': {
    title: '가져온 디자인은 동작 방식까지 알려주지 않습니다',
    explanation:
      '디자인 자료는 화면이 어떻게 보이는지는 알려주지만, 데이터를 어떻게 불러오고 오류를 어떻게 처리할지는 알려주지 않습니다. 이 부분은 따로 정의해야 합니다.',
  },
  'external-design-library-bundle-unsafe': {
    title: '가져온 자료 묶음을 안전하게 확인할 수 없습니다',
    explanation:
      '검색해 가져온 디자인 자료 묶음이 안전하게 읽을 수 있는 형태가 아닙니다. 자료를 다시 가져와 보세요.',
  },
  'external-design-authority-ref-contamination': {
    title: '가져온 자료가 검증된 자료처럼 표시되고 있습니다',
    explanation:
      '아직 검증되지 않은 외부 참조가 이미 검증을 마친 자료의 자리에 들어가 있습니다. 안전을 위해 막았습니다.',
  },
} satisfies Record<ExternalDesignKnowledgeLibraryGapCode, MachineGapCopy>

/**
 * `UxApplicationLibraryAdmissionIssueCode` 24종 — 정본:
 * packages/cogo-kg-browser/src/ux-application-library-admission.ts:11-35
 *
 * 이 중 16개 코드는 `LibraryGapCode` 와 문자 그대로 같은 이름을 공유한다(같은
 * 개념을 RDF/SHACL 경로와 프로그램 방식 컴파일러 경로 양쪽에서 검증하기
 * 때문 — 예: `library-source-forbidden`). 문구가 갈라지지 않도록 새로 쓰지
 * 않고 `LIBRARY_GAP_COPY` 의 항목을 그대로 참조한다.
 */
const UX_APPLICATION_ADMISSION_ISSUE_COPY = {
  'rdf-input-invalid': {
    title: '검증에 필요한 데이터를 읽을 수 없습니다',
    explanation: '화면 구조를 검증하기 위한 원본 데이터가 손상되었거나 읽을 수 있는 형식이 아닙니다.',
  },
  'authority-context-invalid': {
    title: '권리 확인에 필요한 정보가 올바르지 않습니다',
    explanation: '어떤 소스가 이미 승인되었는지 알려주는 정보 자체가 형식에 맞지 않습니다.',
  },
  'ontology-root-missing': {
    title: '화면 정의의 핵심 구성이 빠져 있습니다',
    explanation: '화면 패턴이나 앱 설계 중 반드시 있어야 할 기본 구성 요소가 하나도 없습니다.',
  },
  'shacl-conformance-failed': {
    title: '화면 구조가 정해진 규격에 맞지 않습니다',
    explanation: '화면을 이루는 데이터 구조가 시스템이 요구하는 규격과 다릅니다.',
  },
  'library-source-forbidden': LIBRARY_GAP_COPY['library-source-forbidden'],
  'library-presentation-forbidden': LIBRARY_GAP_COPY['library-presentation-forbidden'],
  'library-event-invalid': LIBRARY_GAP_COPY['library-event-invalid'],
  'library-capability-invalid': LIBRARY_GAP_COPY['library-capability-invalid'],
  'duplicate-ux-instance-id': LIBRARY_GAP_COPY['duplicate-ux-instance-id'],
  'duplicate-library-port-id': LIBRARY_GAP_COPY['duplicate-library-port-id'],
  'unbound-pattern-port': LIBRARY_GAP_COPY['unbound-pattern-port'],
  'incompatible-pattern-port': LIBRARY_GAP_COPY['incompatible-pattern-port'],
  'ux-pattern-state-invalid': LIBRARY_GAP_COPY['ux-pattern-state-invalid'],
  'ux-pattern-transition-invalid': LIBRARY_GAP_COPY['ux-pattern-transition-invalid'],
  'journey-entry-invalid': LIBRARY_GAP_COPY['journey-entry-invalid'],
  'journey-terminal-missing': LIBRARY_GAP_COPY['journey-terminal-missing'],
  'journey-step-unreachable': LIBRARY_GAP_COPY['journey-step-unreachable'],
  'journey-recovery-invalid': LIBRARY_GAP_COPY['journey-recovery-invalid'],
  'exact-namespace-collision': LIBRARY_GAP_COPY['exact-namespace-collision'],
  'exact-namespace-invalid': {
    title: '화면 구성 요소의 내부 식별 값이 어긋나 있습니다',
    explanation:
      '화면 구성 요소를 구분하는 내부 식별값이, 이 화면이 속한 위치와 맞지 않게 생성되었습니다.',
  },
  'application-envelope-invalid': {
    title: '앱 전체 정의가 하나로 정리되지 않았습니다',
    explanation:
      '이 앱을 정의하는 최상위 정보가 없거나 두 개 이상 겹쳐 있거나, 서로 다른 정의가 같은 이름을 공유하고 있습니다.',
  },
  'duplicate-ux-pattern-ref': LIBRARY_GAP_COPY['duplicate-ux-pattern-ref'],
  'definition-reference-invalid': {
    title: '화면이 참조하는 패턴 정의를 찾을 수 없습니다',
    explanation: '화면 구성 요소가 가리키는 패턴 정의와 정확히 일치하는 것이 없습니다.',
  },
  'application-capability-invalid': {
    title: '외부 연동 기능 목록이 서로 어긋나 있습니다',
    explanation: '이 앱이 외부에 공개하는 기능 목록의 개수와 실제 목록이 서로 맞지 않습니다.',
  },
} satisfies Record<UxApplicationLibraryAdmissionIssueCode, MachineGapCopy>

/**
 * 세 표를 합친 런타임 조회용 테이블. 여기서는 더 이상 exhaustiveness 를
 * 주장하지 않는다 — 그 증명은 위 세 `satisfies` 에서 이미 끝났다. 이 표는
 * 오직 `translateMachineGap` 이 임의의(런타임에 알 수 없는) 문자열 코드로
 * 조회하기 위한 용도다.
 */
const MACHINE_GAP_COPY: Readonly<Record<string, MachineGapCopy>> = {
  ...LIBRARY_GAP_COPY,
  ...EXTERNAL_DESIGN_GAP_COPY,
  ...UX_APPLICATION_ADMISSION_ISSUE_COPY,
}

/** 기계 gap(세 어휘 중 하나)을 사용자 언어 항목으로 접는다. 항상 blocking. */
export function translateMachineGap(gap: MachineGapLike): ReadinessItem {
  const copy = MACHINE_GAP_COPY[gap.code]
  if (!copy) {
    // fail-closed: 표에 없는(향후 추가된) 코드도 절대 조용히 통과시키지 않는다.
    // 코드 문자열을 title 이 아니라 explanation 에 넣는 것은 "title 은 기계
    // 코드 금지" 원칙의 의도적 예외다(리뷰 adjudicated-keep) — 이 분기는
    // 번역 표가 아직 모르는 코드이므로 지원 문의 시 상담원이 무엇을 찾아야
    // 하는지 식별할 유일한 단서가 이 코드뿐이다. title 은 여전히 순수
    // 한국어(UNKNOWN_GAP_TITLE)를 유지해 원칙을 지키고, machineCode 필드도
    // 이미 같은 값을 구조적으로 노출하므로 explanation 의 코드는 "또 하나의
    // 노출 지점"이 아니라 사람이 읽는 문장 안에 그 식별자를 자연스럽게
    // 풀어 쓴 것이다.
    return {
      kind: 'blocking',
      title: UNKNOWN_GAP_TITLE,
      explanation: `점검기가 이 항목을 아직 설명하지 못합니다. 문의 시 이 코드를 알려주세요: ${gap.code}`,
      machineCode: gap.code,
      evidenceRefs: [...gap.evidenceRefs],
    }
  }
  return {
    kind: 'blocking',
    title: copy.title,
    explanation: copy.explanation,
    machineCode: gap.code,
    suggestion: copy.suggestion,
    evidenceRefs: [...gap.evidenceRefs],
  }
}

/** gap 인 축만 화면 항목이 된다 — 채워진 축(from-source 등)은 보고할 것이 없다. */
export function translateAxisResolution(
  axis: AxisResolution,
  copy: DesignAxesCopy = KO_DESIGN_AXES_COPY,
): ReadinessItem | null {
  if (axis.origin !== 'gap') return null
  return {
    kind: 'unfilled',
    title: copy.notDecidedTitle(copy.axisLabels[axis.axis]),
    explanation: axis.explanation, // 축이 이미 지닌 설명을 그대로 — 재작성 금지
    suggestion: { label: copy.decideInDesignSystem, navigateTo: '/studio/design-system' },
    evidenceRefs: [],
  }
}

/** 권고는 구조적으로 advisory 이며, 어떤 severity 든 blocking 이 될 수 없다. */
export function translateAdvice(
  axis: AxisName,
  advice: Advice,
  copy: DesignAxesCopy = KO_DESIGN_AXES_COPY,
): ReadinessItem {
  return {
    kind: 'advisory',
    title: copy.adviceTitle(copy.axisLabels[axis]),
    explanation: advice.message,
    suggestion: advice.suggestion
      ? { label: copy.applySuggestion(advice.suggestion.value) }
      : undefined,
    evidenceRefs: [],
  }
}
